import { defineStore } from 'pinia';
import { pinia } from '@/store/pinia';
import { useAccountInfo } from '@models/account/useAccountInfo';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import { useYoutube } from '@hooks/useYoutube';
import numberUtils from '@utils/number.utils';
import projectSettingApi from '../apis/projectSetting.api';
import PtMessageBox from '@ui/message-box';
import styles from '@/pages/master/views/guide/guide.module.scss';
import useEventBus from '@assets/plugins/eventBus';
import i18n from '@assets/i18n/index';
import useLocalData from '@hooks/useLocalData';
import {
    hideSendInBlue,
    closeIntercomNotifications,
    operation
} from '@assets/services/intercom.service';
import analyticsService from '@assets/services/analytics.service';
import { getAreaForUrl } from '@/hooks/useHelpLinks';

export type GuideKey =
    | 'startGuide_abTest'
    | 'startGuide_dataCenter'
    | 'startGuide_experiencePublish'
    | 'startGuide_goal'
    | 'startGuide_heatmap'
    | 'startGuide_inline'
    | 'startGuide_popup'
    | 'startGuide_tagInstall'
    | 'startGuide_video';

export interface GuideStep {
    key: GuideKey;
    title: string;
    done: boolean;
    doneTip: string;
    componentName: string;
}

interface GuideCategory {
    key: string;
    title: string;
    icon: string;
    steps: GuideStep[];
    activeStepKey: GuideStep['key'] | '';
}

type QueryState = 'loading' | 'success' | 'failed' | null;
interface State {
    guideOnboardingTypeName: 'startGuide';
    guideVisible: boolean; //当前气泡展示状态，和Guide.vue中的hideGuide逻辑一致
    categories: GuideCategory[];
    fetchCategoryListQueryState: QueryState;
    fetchCategoryListSid: string;
    activeCategoryKey: string;
    guidePopoverVisible: boolean;
    guideClosed: boolean; //是否点击过关闭guide按钮
    helpPopoverVisible: boolean;
    conversionDialogVisible: boolean;
    visibleGuideIcon: boolean; //临时隐藏guide icon 入口
    autoShowPublishGuide: boolean; // 是否需要自动展开publish guide（刚创建完以上三步的）
}

export const useGuideStore = defineStore('userGuide', {
    state: (): State => {
        return {
            guideOnboardingTypeName: 'startGuide',
            guideVisible: false,
            activeCategoryKey: '',
            fetchCategoryListQueryState: null,
            fetchCategoryListSid: '',
            categories: [
                {
                    key: 'started',
                    title: 'guide.guide_popover_category_1',
                    icon: 'pt-icon-integration',
                    activeStepKey: '',
                    steps: [
                        {
                            key: 'startGuide_video',
                            title: 'guide.guide_popover_step_1',
                            done: false,
                            doneTip: 'guide.guide_popover_step_1_done',
                            componentName: 'GuideStep11'
                        },
                        {
                            key: 'startGuide_tagInstall',
                            title: 'guide.guide_popover_step_2',
                            done: false,
                            doneTip: 'guide.guide_popover_step_2_done',
                            componentName: 'GuideStep12'
                        },
                        {
                            key: 'startGuide_goal',
                            title: 'guide.guide_popover_step_3',
                            done: false,
                            doneTip: 'guide.guide_popover_step_3_done',
                            componentName: 'GuideStep13'
                        }
                    ]
                },
                {
                    key: 'insight',
                    title: 'guide.guide_popover_category_2',
                    icon: 'pt-icon-integration',
                    activeStepKey: '',
                    steps: [
                        {
                            key: 'startGuide_heatmap',
                            title: 'guide.guide_popover_step_4',
                            done: false,
                            doneTip: 'guide.guide_popover_step_4_done',
                            componentName: 'GuideStep21'
                        },
                        {
                            key: 'startGuide_dataCenter',
                            title: 'guide.guide_popover_step_5',
                            done: true,
                            doneTip: 'guide.guide_popover_step_5_done',
                            componentName: 'GuideStep22'
                        }
                    ]
                },
                {
                    key: 'experience',
                    title: 'guide.guide_popover_category_3',
                    icon: 'pt-icon-integration',
                    activeStepKey: '',
                    steps: [
                        {
                            key: 'startGuide_popup',
                            title: 'guide.guide_popover_step_6',
                            done: false,
                            doneTip: 'guide.guide_popover_step_6_done',
                            componentName: 'GuideStep31'
                        },
                        {
                            key: 'startGuide_inline',
                            title: 'guide.guide_popover_step_7',
                            done: false,
                            doneTip: 'guide.guide_popover_step_7_done',
                            componentName: 'GuideStep32'
                        },
                        {
                            key: 'startGuide_abTest',
                            title: 'guide.guide_popover_step_8',
                            done: false,
                            doneTip: 'guide.guide_popover_step_8_done',
                            componentName: 'GuideStep33'
                        },
                        {
                            key: 'startGuide_experiencePublish',
                            title: 'guide.guide_popover_step_9',
                            done: false,
                            doneTip: 'guide.guide_popover_step_9_done',
                            componentName: 'GuideStep34'
                        }
                    ]
                }
            ],
            guidePopoverVisible: false,
            guideClosed: false,
            helpPopoverVisible: false,
            conversionDialogVisible: false,
            visibleGuideIcon: true,
            autoShowPublishGuide: false
        };
    },
    getters: {
        // 所有二级分类配置信息
        allSteps: $state => {
            const allSteps: GuideStep[] = $state.categories.reduce((acc: GuideStep[], cur) => {
                acc.push(...cur.steps);
                return acc;
            }, []);
            return allSteps;
        },

        // 所有experience part的步骤
        allExperienceSteps: $state => {
            const allExperienceSteps: GuideStep[] = $state.categories.reduce(
                (acc: GuideStep[], cur) => {
                    cur.key === 'experience' && acc.push(...cur.steps);
                    return acc;
                },
                []
            );
            return allExperienceSteps;
        },

        // 总完成百分比
        percentage() {
            const allDoneSteps = this.allSteps?.filter(step => step.done);
            return parseInt(numberUtils.getPercent(allDoneSteps.length, this.allSteps.length), 10);
        },

        allStepCompleted() {
            return (this.percentage as unknown as number) === 100;
        },

        // 当前选中的大类别
        currentActiveStep: $state => {
            return (
                $state.activeCategoryKey &&
                $state.categories.find(category => category.key === $state.activeCategoryKey)
            );
        }
    },
    actions: {
        /**
         * 更新每个分类下的默认展开项
         */
        _resetActiveKey(setActive = true) {
            // 更新一级列表选中类别(优先查找非video且未完成的步骤)
            this.$state.activeCategoryKey = setActive
                ? (
                      this.$state.categories.find(category =>
                          category.steps.some(step => !step.done && step.key !== 'startGuide_video')
                      ) || this.$state.categories[0]
                  ).key
                : '';

            // 更新二级列表选中类别
            this.$state.categories.forEach(category => {
                category.activeStepKey = setActive
                    ? (
                          category.steps.find(
                              step => !step.done && step.key !== 'startGuide_video'
                          ) || category.steps[0]
                      ).key
                    : '';
            });
        },

        /**
         * 显示弹框
         */
        _showGuidePopover() {
            // 关闭Help弹框
            this.closeHelpPopup();

            // Guide icon显示
            this.triggerGuideIcon(true);

            // 线上Guide弹框
            this.$state.guidePopoverVisible = true;

            // 关闭Intercom会话及通知
            operation('hide');
            closeIntercomNotifications();
        },

        /**
         * 隐藏弹框
         */
        _hideGuidePopover() {
            this.$state.guidePopoverVisible = false;
        },

        /**
         * 更新入口显示状态
         */
        updateGuideVisibleStatus(status: boolean) {
            this.$state.guideVisible = status;
        },

        updateGuideState(name: keyof State, value: any) {
            this.$state[name] = value;
        },

        /**
         * [接口] 获取完成状态
         */
        fetchGuideStatus() {
            const { profileInfo } = useProfileInfo();
            const { getLocalData } = useLocalData();

            if (
                !profileInfo.value?.sid ||
                (this.$state.fetchCategoryListQueryState !== null &&
                    this.$state.fetchCategoryListSid === profileInfo.value?.sid)
            ) {
                return;
            }

            // 重置初始状态状态
            this.$state.fetchCategoryListSid = profileInfo.value?.sid || '';
            this.$state.activeCategoryKey = '';
            this.$state.guidePopoverVisible = false;
            this.$state.helpPopoverVisible = false;

            // 获取本地存储的完成状态，如果已全部完成就不再取数
            const localData = getLocalData('startGuideClosed');
            this.$state.guideClosed = Boolean(localData.guideClosed);
            if (this.$state.guideClosed) {
                this.allSteps.forEach(step => (step.done = true));
                this.$state.fetchCategoryListQueryState = 'success';
                return;
            }

            // 请求前重置初始状态状态
            this.$state.fetchCategoryListQueryState = 'loading';
            this.allSteps.forEach(step => (step.done = false));

            projectSettingApi
                .firstGuideProfile({
                    profileId: profileInfo.value?.sid
                })
                .then(
                    (
                        guides: Array<{
                            status: number;
                            guideKey: GuideKey;
                        }>
                    ) => {
                        const stepKeys = this.allSteps.map(step => step.key);
                        const updateList: GuideKey[] = guides
                            .filter(item => stepKeys.includes(item.guideKey) && item.status === 1)
                            .map(item => item.guideKey);

                        this.updateStateStepsStatus(updateList);
                        this._resetActiveKey();
                        this.$state.fetchCategoryListQueryState = 'success';
                    },
                    err => {
                        this.$state.fetchCategoryListQueryState = 'failed';
                    }
                );
        },

        /**
         * [接口] 更新完成状态
         */
        updateStepsStatus(stepKeys: GuideKey[]) {
            const { profileInfo } = useProfileInfo();
            const guides = stepKeys.reduce(
                (acc, cur) => {
                    acc.push({
                        guideKey: cur,
                        status: 1
                    });
                    return acc;
                },
                [] as Array<{
                    guideKey: GuideKey;
                    status: 1;
                }>
            );
            if (guides.length === 0 || !profileInfo.value?.sid) {
                return;
            }

            if (
                stepKeys.some(key =>
                    ['startGuide_popup', 'startGuide_inline', 'startGuide_abTest'].includes(key)
                )
            ) {
                this.$state.autoShowPublishGuide = true;
            }

            return projectSettingApi
                .updateFirstGuideProfile({
                    input: {
                        profileId: profileInfo.value.sid,
                        guides
                    }
                })
                .then(
                    res => {
                        this.updateStateStepsStatus(stepKeys);
                        return Promise.resolve();
                    },
                    err => {
                        return Promise.reject(err);
                    }
                );
        },

        /**
         * 更新 $state 中步骤的完成状态
         */
        updateStateStepsStatus(stepKeys: GuideKey[]) {
            this.$state.categories.forEach(category => {
                category.steps.forEach(step => {
                    if (stepKeys.includes(step.key)) {
                        step.done = true;
                    }
                });
            });
        },

        /**
         * 获取第一个未完成的一级分类
         */
        getNextStep() {
            return this.$state.categories.find(category => category.steps.some(step => !step.done));
        },

        /***
         * 更新所有一级分类中 [手风琴组件] 的选中状态值
         */
        updateChildStepActive() {
            this.$state.categories.forEach(category => {
                category.activeStepKey = (
                    category.steps.find(step => !step.done) || category.steps[0]
                ).key;
            });
        },

        /**
         * 获取指定一级分类下的完成百分比数值
         */
        getStepProgress(step: GuideCategory): number {
            return parseInt(
                numberUtils.getPercent(
                    step.steps.filter(step => step.done).length,
                    step.steps.length
                ),
                10
            );
        },

        /**
         * 获取指定步骤的配置信息
         */
        getStepConfig(stepKey: GuideKey): GuideStep | undefined {
            return this.allSteps.find(step => step.key === stepKey);
        },

        /**
         * 展开指定一级分类
         */
        gotoCategory(categoryKey: string) {
            const nextCategory = this.$state.categories.find(
                category => category.key === categoryKey
            );

            if (nextCategory) {
                this.$state.activeCategoryKey = nextCategory.key;
                this.updateChildStepActive();

                // 显示弹框
                this._showGuidePopover();
            }
        },

        /**
         * 展开指定步骤
         */
        gotoStep(stepKey: GuideKey) {
            const targetCategory = this.$state.categories.find(category => {
                return category.steps.find(step => step.key === stepKey);
            });
            if (targetCategory) {
                this.$state.activeCategoryKey = targetCategory.key;
                targetCategory.activeStepKey = stepKey;
            }

            // 显示弹框
            this._showGuidePopover();
        },

        /**
         * 显示Guide气泡弹框
         */
        showGuidePopover(
            trigger?: 'helpClick' | 'guideIconClick' | 'videoClose' | 'firstVerifySuccess'
        ) {
            if (!this.$state.guideVisible) {
                return;
            }
            // 更新选中项
            // Help 选项中打开时，默认全部收起
            this._resetActiveKey(trigger !== 'helpClick');

            // 显示弹框
            this._showGuidePopover();
        },

        /**
         * 隐藏Guide气泡弹框
         */
        hideGuidePopover() {
            if (!this.$state.guidePopoverVisible) return;

            // 清除当前Onbo蓝泡泡
            const { eventBus } = useEventBus();
            eventBus.$emit('hideTip');

            this._hideGuidePopover();
        },

        /**
         * 关闭问号弹框
         * 在线上档案 Experience 内创建
         */
        closeHelpPopup() {
            const helpIcon = document.querySelector('.pt-help');
            helpIcon && helpIcon.classList.remove('active');
        },

        /**
         * Guide气泡弹框开关
         */
        triggerGuidePopover() {
            this.$state.guidePopoverVisible
                ? this.hideGuidePopover()
                : this.showGuidePopover('guideIconClick');
            this.hideHelpPopover();

            this.trackEvent('guide_click', {
                position: 'floating',
                complete_rate: this.percentage + '%'
            });
        },

        /**
         * Guide Icon开关
         */
        triggerGuideIcon(status?: boolean) {
            this.$state.visibleGuideIcon = status ?? !this.$state.visibleGuideIcon;
        },

        /**
         * 显示Guide气泡弹框
         */
        showHelpPopover() {
            operation('hide');
            closeIntercomNotifications();
            this.$state.helpPopoverVisible = true;
        },

        /**
         * 隐藏Help气泡弹框
         * 关闭气泡时，需要隐藏 sendInBlue
         */
        hideHelpPopover() {
            this.$state.helpPopoverVisible = false;
            hideSendInBlue();
        },

        /**
         * Help气泡弹框开关
         */
        triggerHelpPopover() {
            this.$state.helpPopoverVisible ? this.hideHelpPopover() : this.showHelpPopover();
            this.hideGuidePopover();
        },

        /**
         * 显示视频弹框
         */
        showVideoPopup(from?: 'createProfile') {
            const { eventBus } = useEventBus();
            const area = getAreaForUrl();
            const videoSrc =
                area === 'JP'
                    ? 'https://www.youtube.com/embed/CVwHE5ZYoiE'
                    : 'https://www.youtube.com/embed/RNBTQPRDFB8';
            const iframeId = 'js_guide-video';
            const iframeHeight = '310px';

            // trackEventPosition
            const position = from === 'createProfile' ? 'welcome_screen' : 'guide';

            PtMessageBox({
                showClose: true,
                showConfirmButton: from === 'createProfile',
                showCancelButton: false,
                dangerouslyUseHTMLString: true,
                closeOnClickModal: false,
                confirmButtonText: i18n.global.t('guide.welcome_ptengine_btn'),
                customClass:
                    from === 'createProfile' ? styles.guideMessageBox : styles.guideVideoMessageBox,
                title: i18n.global.t(
                    from === 'createProfile'
                        ? 'guide.welcome_ptengine'
                        : 'guide.guide_popover_step_1_video_title'
                ),
                message: `<div class=${styles.guideDesc}>
                    ${i18n.global.t('guide.welcome_ptengine_desc')}
                </div>
                    <div class=${styles.guideVideo}>
                        <div id="${iframeId}" style="min-height: ${iframeHeight};"></div>
                        <div data-loading class="pt-loading-mask">
                            <div class="pt-loading-spinner">
                                <svg class="circular" viewBox="25 25 50 50"><circle cx="50" cy="50" r="14" fill="none" stroke="#d2d6dd" stroke-width="2" stroke-dasharray="65.97344572538566 23.991148575128552" style="animation-play-state: running; animation-delay: 0s;"></circle></svg>
                            </div>
                        </div>
                    </div>`,
                callback: (action, instance) => {
                    // 由档案创建过来的自动弹出视频，关闭后展开下个步骤
                    if (from === 'createProfile') {
                        this.gotoStep('startGuide_tagInstall');
                        eventBus.$emit('showTip', this.$state.guideOnboardingTypeName);
                    } else {
                        this.showGuidePopover('videoClose');
                    }

                    // track event
                    this.trackEvent('guide_video_popup_cta_click', {
                        position
                    });
                }
            });

            setTimeout(() => {
                const el = document.querySelector('#' + iframeId);
                if (el) {
                    useYoutube({
                        target: el,
                        youtubeSrc: videoSrc,
                        width: '100%',
                        height: '100%',
                        stateChangeCallBack: (state: number) => {
                            if (state === 1) {
                                !this.getStepConfig('startGuide_video')?.done &&
                                    this.updateStepsStatus(['startGuide_video']);

                                // track event
                                this.trackEvent('guide_video_play', {
                                    position
                                });
                            }

                            if (state === 2) {
                                // track event
                                this.trackEvent('guide_video_pause', {
                                    position
                                });
                            }
                        }
                    });
                }
            }, 400);
        },
        /**
         * 发送事件
         */
        trackEvent(name: string, property?: any) {
            analyticsService.usePTX(name, property).track();
        },
        /**
         * 打开弹出热图demo链接
         */
        openHeatmapDemo() {
            const { account } = useAccountInfo();
            const { profileInfo } = useProfileInfo();
            const url = `${
                account.value?.area === 'EN'
                    ? import.meta.env.VITE_HEATMAP_DEMO_URL_EN
                    : import.meta.env.VITE_HEATMAP_DEMO_URL
            }&pt_sid=${
                profileInfo.value?.sid
            }&pt_lang=${account.value?.language?.toLocaleLowerCase()}`;
            window.open(url, '_blank');
            this.trackEvent('guide_hm_demo_click');
        },
        /**
         * 打开编辑器demo链接
         */
        openEditorDemo() {
            const { account } = useAccountInfo();
            const editorDemoUrl = `${
                account.value?.area?.toLocaleUpperCase() === 'EN'
                    ? import.meta.env.VITE_EXPERIENCE_EDITOR_DEMO_URL_EN
                    : import.meta.env.VITE_EXPERIENCE_EDITOR_DEMO_URL
            }&pt_lang=${account.value?.language?.toLocaleLowerCase()}`;
            if (editorDemoUrl) {
                window.open(editorDemoUrl as string);
            }
            this.trackEvent('inline_demo_click');
        }
    }
});

export function useGuideOutsideStore() {
    return useGuideStore(pinia);
}
